<template>
	<div class="tombstone-popover">
		<div :class="overlayClasses"><!-- intentionally empty --></div>
		<div :class="popoverClasses">
			<div @click="onBackgroundClick" @touchstart.passive="onBackgroundClick"><!-- intentionally empty --></div>
			<div class="tombstone-popover__body">
				<slot></slot>
			</div>
		</div>
	</div>
</template>

<script>
import {
	Enum,
	PopoverVisibility
} from "@teaming/common-enums";

export const HeightRatio = Enum.create( {
	ONE_HALF: "one-half",
	TWO_THIRDS: "two-thirds",
	THREE_QUARTERS: "three-quarters",
	FOUR_FIFTHS: "four-fifths",
	NINE_TENTHS: "nine-tenths",
	DEFAULT: "four-fifths",
} );

export default {
	name: "TombstonePopover",
	props: {
		popoverVisibility: {
			type: String,
			default: PopoverVisibility.INDETERMINATE,
			validator: prop => PopoverVisibility.includes( prop ),
		},
		heightRatio: {
			type: String,
			default: HeightRatio.DEFAULT,
			validator: value => HeightRatio.includes( value ),
		},
	},
	computed: {
		overlayClasses() {
			const { popoverVisibility, } = this;
			return {
				"tombstone-popover__overlay": true,
				"tombstone-popover__overlay--hidden": popoverVisibility === PopoverVisibility.HIDDEN,
				"tombstone-popover__overlay--visible": popoverVisibility === PopoverVisibility.VISIBLE,
			};
		},
		popoverClasses() {
			const { popoverVisibility, heightRatio, } = this;
			return {
				"tombstone-popover__face": true,
				[ `tombstone-popover__face--${ heightRatio }` ]: true,
				"tombstone-popover__face--hidden": popoverVisibility === PopoverVisibility.HIDDEN,
				"tombstone-popover__face--visible": popoverVisibility === PopoverVisibility.VISIBLE,
			};
		},
	},
	methods: {
		onBackgroundClick( e ) {
			e.preventDefault();
			e.stopPropagation();
			this.$emit( "close" );
		},
	},
};
</script>

<style lang="scss" scoped>
$overlayBackgroundColor: $black;
$bodyBackgroundColor: $white;

.tombstone-popover {
	// NOTE: to hide this popover at a certain breakpoint,
	// assign a class name to the className prop and set
	// its display to none at the desired breakpoint, e.g.:
	//
	// .my-component__tombstone-popover {
	//		@include for-size-and-larger('md') {
	//			display: none;
	//		}
	// }
	//
	// <TombstonePopover class="my-component__tombstone-popover">
	// ...
	// </TombstonePopover>
}

@keyframes slide-up {
	0% {
		top: 110%;
	}
	100% {
		top: 0;
	}
}

@keyframes slide-down {
	0% {
		top: 0;
	}
	100% {
		top: 110%;
	}
}

$opacity-none: 0;
$opacity-dim: 0.3;

@keyframes fade-in {
	0% {
		opacity: $opacity-none;
	}
	100% {
		opacity: $opacity-dim;
	}
}

@keyframes fade-out {
	0% {
		opacity: $opacity-dim;
	}
	100% {
		opacity: $opacity-none;
	}
}

.tombstone-popover__overlay {
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	width: 100%;
	z-index: 5;
	background-color: $overlayBackgroundColor;
	opacity: $opacity-none;
	display: none;

	@include for-size-and-larger('md') {
		display: none;
	}
}

.tombstone-popover__overlay--hidden {
	animation-name: fade-out;
	animation-timing-function: ease-in;
	animation-duration: 250ms;
	animation-fill-mode: forwards;
	display: none;
}

.tombstone-popover__overlay--visible {
	animation-name: fade-in;
	animation-timing-function: ease-in;
	animation-duration: 250ms;
	animation-fill-mode: forwards;
	display: block;
}

.tombstone-popover__face {
	position: fixed;
	top: 110%;
	left: 0;
	bottom: 0;
	width: 100%;
	z-index: 10;
	display: flex;
	flex-direction: column;

	div:first-of-type {
		flex: 1; // visible overlay space
	}

	div:last-of-type {
		flex: 4; // visible body space
		background-color: $bodyBackgroundColor;
		border-radius: 1rem 1rem 0 0;
		padding-top: 1rem;
		overflow-y: hidden;
	}
}

.tombstone-popover__face--one-half {
	div:last-of-type {
		flex: 1;
	}
}

.tombstone-popover__face--two-thirds {
	div:last-of-type {
		flex: 2;
	}
}

.tombstone-popover__face--three-quarters {
	div:last-of-type {
		flex: 3;
	}
}

.tombstone-popover__face--four-fifths {
	div:last-of-type {
		flex: 4;
	}
}

.tombstone-popover__face--nine-tenths {
	div:last-of-type {
		flex: 9;
	}
}

.tombstone-popover__face--hidden {
	animation-name: slide-down;
	animation-timing-function: ease-in;
	animation-duration: 250ms;
	animation-fill-mode: forwards;
}

.tombstone-popover__face--visible {
	animation-name: slide-up;
	animation-timing-function: ease-in;
	animation-duration: 250ms;
	animation-fill-mode: forwards;
}

.tombstone-popover__body {
	position: relative;
	text-align: left;
}
</style>
